import { useEffect, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import type { LocationsOverview, StorageLocationTreeNode } from '@/lib/api';
import { Icon, IconButton, Skeleton } from '@/design-system';
import { LOCATION_KIND_ICON } from '@/lib/locations';

export type LocationSelection =
  | { kind: 'all' }
  | { kind: 'unassigned' }
  | { kind: 'pending' }
  | { kind: 'location'; id: string };

export function isSameSelection(a: LocationSelection, b: LocationSelection): boolean {
  if (a.kind !== b.kind) return false;
  return a.kind === 'location' && b.kind === 'location' ? a.id === b.id : true;
}

interface LocationTreeProps {
  overview?: LocationsOverview;
  loading?: boolean;
  selected: LocationSelection;
  onSelect: (selection: LocationSelection) => void;
  /** Opens the "Ny lokasjon" form, optionally pre-filling the parent. */
  onCreateLocation?: (parentId?: string) => void;
  className?: string;
}

/**
 * The Kjeller sidebar's location tree — "Alle viner" / "Uplassert" /
 * "Venter på levering" as system rows, then the Room → Cabinet/Fridge →
 * Shelf hierarchy with expand/collapse chevrons and per-node bottle counts.
 * Presentational + selection-only: no fetching, no routing — the caller
 * (cellar.tsx) owns the query and the URL state, and renders this either in
 * a fixed sidebar (desktop) or inside a Modal (mobile sheet).
 */
export function LocationTree({ overview, loading, selected, onSelect, onCreateLocation, className }: LocationTreeProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  // Auto-expand the ancestor chain of the selected node so it's visible.
  useEffect(() => {
    if (!overview || selected.kind !== 'location') return;
    const ancestors = ancestorChain(overview.tree, selected.id);
    if (ancestors.length) {
      setExpanded((prev) => new Set([...prev, ...ancestors]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview, selected.kind === 'location' ? selected.id : undefined]);

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (loading || !overview) {
    return (
      <div className={clsx('grid gap-1.5', className)}>
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} height={32} rounded="sm" />
        ))}
      </div>
    );
  }

  return (
    <nav className={clsx('grid gap-0.5 text-sm', className)} aria-label="Lokasjoner">
      <SystemRow
        icon="sparkles"
        label="Alle viner"
        count={overview.all.totalQty}
        active={selected.kind === 'all'}
        onClick={() => onSelect({ kind: 'all' })}
      />
      <SystemRow
        icon="help-circle"
        label="Uplassert"
        count={overview.unassigned.totalQty}
        active={selected.kind === 'unassigned'}
        onClick={() => onSelect({ kind: 'unassigned' })}
      />
      <SystemRow
        icon="truck"
        label="Venter på levering"
        count={overview.pendingDelivery.totalQty}
        active={selected.kind === 'pending'}
        onClick={() => onSelect({ kind: 'pending' })}
      />

      <div className="my-1.5 h-px bg-[color:var(--ui-color-line-soft)]" />

      {overview.tree.length === 0 ? (
        <p className="px-2 py-3 text-xs text-mist">Ingen lokasjoner ennå.</p>
      ) : (
        overview.tree.map((node) => (
          <LocationNodeRow
            key={node.id}
            node={node}
            depth={0}
            expanded={expanded}
            onToggle={toggle}
            selected={selected}
            onSelect={onSelect}
          />
        ))
      )}

      {onCreateLocation && (
        <button
          type="button"
          onClick={() => onCreateLocation(undefined)}
          className="mt-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs uppercase tracking-widest text-mist hover:text-candle"
        >
          <Icon name="plus" size={13} />
          Ny lokasjon
        </button>
      )}
    </nav>
  );
}

function SystemRow({
  icon,
  label,
  count,
  active,
  onClick,
}: {
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
        active ? 'bg-[color:var(--ui-color-candle)]/12 text-candle' : 'text-parchment hover:bg-ink-soft',
      )}
    >
      <Icon name={icon} size={15} className={active ? 'text-candle' : 'text-mist'} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count > 0 && <span className="text-[11px] text-mist">{count}</span>}
    </button>
  );
}

function LocationNodeRow({
  node,
  depth,
  expanded,
  onToggle,
  selected,
  onSelect,
}: {
  node: StorageLocationTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  selected: LocationSelection;
  onSelect: (selection: LocationSelection) => void;
}) {
  const isOpen = expanded.has(node.id);
  const isActive = selected.kind === 'location' && selected.id === node.id;
  const hasChildren = node.children.length > 0;

  return (
    <div>
      <div
        className={clsx(
          'flex items-center gap-1 rounded-md pr-2 transition-colors',
          isActive ? 'bg-[color:var(--ui-color-candle)]/12 text-candle' : 'text-parchment hover:bg-ink-soft',
        )}
        style={{ paddingLeft: `${0.5 + depth * 1.1}rem` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(node.id)}
            aria-label={isOpen ? `Skjul ${node.name}` : `Vis ${node.name}`}
            className="grid h-5 w-5 shrink-0 place-items-center text-mist hover:text-candle"
          >
            <Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={13} />
          </button>
        ) : (
          <span className="h-5 w-5 shrink-0" />
        )}
        <button
          type="button"
          onClick={() => onSelect({ kind: 'location', id: node.id })}
          className="flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left"
        >
          <Icon name={LOCATION_KIND_ICON[node.kind]} size={14} className={isActive ? 'text-candle' : 'text-mist'} />
          <span className="min-w-0 flex-1 truncate">{node.name}</span>
          {node.totalQty > 0 && <span className="text-[11px] text-mist">{node.totalQty}</span>}
        </button>
      </div>
      {hasChildren && isOpen && (
        <div>
          {node.children.map((child) => (
            <LocationNodeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              selected={selected}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Ids of every ancestor of `id` (root-first, not including `id` itself), or `[]` if not found. */
function ancestorChain(nodes: StorageLocationTreeNode[], id: string, trail: string[] = []): string[] {
  for (const node of nodes) {
    if (node.id === id) return trail;
    const found = ancestorChain(node.children, id, [...trail, node.id]);
    if (found.length > 0 || node.children.some((child) => child.id === id)) return found;
  }
  return [];
}

/** Human label for a location selection — used by the mobile trigger button + stats header. */
export function useSelectionLabel(overview: LocationsOverview | undefined, selection: LocationSelection): string {
  return useMemo(() => {
    if (selection.kind === 'all') return 'Alle viner';
    if (selection.kind === 'unassigned') return 'Uplassert';
    if (selection.kind === 'pending') return 'Venter på levering';
    if (!overview) return 'Lokasjon';
    return findNodeName(overview.tree, selection.id) ?? 'Lokasjon';
  }, [overview, selection]);
}

function findNodeName(nodes: StorageLocationTreeNode[], id: string): string | undefined {
  for (const node of nodes) {
    if (node.id === id) return node.name;
    const found = findNodeName(node.children, id);
    if (found) return found;
  }
  return undefined;
}
